import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { MainLayout } from "@/layouts/MainLayout";
import { PublicLayout } from "@/layouts/PublicLayout";
import Index from "@/pages/Index";
import NewLanding from "@/pages/NewLanding";
import AdsLanding from "@/pages/AdsLanding";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import RoleSelection from "@/pages/RoleSelection";
import EmailConfirmed from "@/pages/EmailConfirmed";
import PasswordReset from "@/pages/PasswordReset";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyOtp from "@/pages/VerifyOtp";
import { CalendlyCallback } from "@/pages/auth/CalendlyCallback";
import Profile from "@/pages/Profile";
import JobCreate from "@/pages/JobCreate";
import Job from "@/pages/Job";
import Billing from "@/pages/Billing";
import JobList from "@/components/job/JobList";
import TestCreator from "@/pages/TestCreator";
import TestDetails from "@/pages/TestDetails";
import TestManagement from "@/pages/TestManagement";
import { TestTaker } from "@/components/test/TestTaker";
import { TestStartHandler } from "@/components/test/TestStartHandler";
import { TestResponseView } from "@/components/test-details/TestResponseView";
import { TestInviteHandler } from "@/components/test/TestInviteHandler";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MainNav } from "@/components/MainNav";
import TestHistoryPage from "@/pages/TestHistoryPage";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import TestSubmissionSuccess from "@/pages/TestSubmissionSuccess";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import ContactUs from "@/pages/ContactUs";
import AboutUs from "@/pages/AboutUs";
import Pricing from "@/pages/Pricing";
import Blog from "@/pages/Blog";
import { DemoSuccessPage } from "@/components/landing/DemoSuccessPage";
import { InterviewAvailability } from "@/pages/InterviewAvailability";
import { logAuthState } from "@/utils/debug-auth";
import { trackGoogleAuth } from "@/utils/auth-tracking";
import { PreviewWarningDialog } from "@/components/test/PreviewWarningDialog";
import InnoventureLogin from "@/pages/InnoventureLogin";
import InnoventureApp from "@/pages/InnoventureApp";
import InnoventureProfile from "@/pages/InnoventureProfile";
import InnoventureTestSuccess from "@/pages/InnoventureTestSuccess";
import InnoventureTestDetails from "@/pages/InnoventureTestDetails";
import BulkUserImport from "@/pages/BulkUserImport";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function TestTakerWrapper() {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlResponseId = searchParams.get('responseId');
  const [testResponseId, setTestResponseId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showPreviewWarning, setShowPreviewWarning] = useState(false);
  const [previousResponseCount, setPreviousResponseCount] = useState(0);
  const [isUserPreview, setIsUserPreview] = useState(false);

  const createNewTestResponse = async () => {
    console.log('[TestTakerWrapper] Creating new test response');
    const { data, error } = await supabase
      .from('test_responses')
      .insert({
        test_id: testId,
        candidate_id: user.id,
        status: 'in_progress',
        started_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505' && error.message.includes('unique_in_progress_response')) {
        console.log('[TestTakerWrapper] Duplicate response detected, fetching the existing one');
        
        const { data: retryExistingResponse, error: retryError } = await supabase
          .from('test_responses')
          .select()
          .eq('test_id', testId)
          .eq('candidate_id', user.id)
          .eq('status', 'in_progress')
          .maybeSingle();
          
        if (retryError || !retryExistingResponse) {
          console.error('[TestTakerWrapper] Error fetching existing response after constraint violation:', retryError);
          toast({
            title: "Error",
            description: "Failed to start test. Please try again.",
            variant: "destructive",
          });
          return;
        }
        
        console.log('[TestTakerWrapper] Using existing response after constraint check:', retryExistingResponse);
        setTestResponseId(retryExistingResponse.id);
        setIsInitialized(true);
        return;
      }
      
      console.error('[TestTakerWrapper] Error creating test response:', error);
      toast({
        title: "Error",
        description: "Failed to start test. Please try again.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('[TestTakerWrapper] Successfully created test response:', data);
    setTestResponseId(data.id);
    setIsInitialized(true);
  };

  const handlePreviewWarningConfirm = async () => {
    setShowPreviewWarning(false);
    await createNewTestResponse();
  };

  const handlePreviewWarningCancel = () => {
    setShowPreviewWarning(false);
    window.history.back();
  };

  useEffect(() => {
    console.log('[TestTakerWrapper] Auth state dependency triggered', { user, isInitialized, urlResponseId });
    
    if (!user || isInitialized) {
      return;
    }

    const createTestResponse = async () => {
      if (!testId) {
        console.log('[TestTakerWrapper] No testId provided');
        return;
      }

      try {
        // PRIORITY 1: Check if responseId is provided in URL
        if (urlResponseId) {
          console.log('[TestTakerWrapper] Found responseId in URL:', urlResponseId);
          
          const { data: response, error } = await supabase
            .from('test_responses')
            .select(`
              id, 
              candidate_id, 
              status, 
              test_id,
              tests!inner (
                scheduled_end_time,
                schedule_timezone
              )
            `)
            .eq('id', urlResponseId)
            .eq('candidate_id', user.id)
            .eq('test_id', testId)
            .eq('status', 'in_progress')
            .maybeSingle();

          if (error) {
            console.error('[TestTakerWrapper] Error verifying URL responseId:', error);
            toast({
              title: "Error",
              description: "Could not resume test. Starting fresh.",
              variant: "destructive",
            });
          } else if (response) {
            // Check if scheduled_end_time has passed
            if (response.tests?.scheduled_end_time) {
              const endTime = new Date(response.tests.scheduled_end_time);
              const now = new Date();
              
              if (now > endTime) {
                console.log('[TestTakerWrapper] Test window has closed');
                toast({
                  title: "Test Window Closed",
                  description: "This test window has closed and can no longer be resumed.",
                  variant: "destructive",
                });
                // Context-aware navigation for test window closed
                const { data: testOrgData } = await supabase
                  .from('tests')
                  .select('organization_id')
                  .eq('id', testId)
                  .single();
                const isInnoventure = testOrgData?.organization_id === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
                navigate(isInnoventure ? `/innoventure-test/${testId}` : '/dashboard');
                return;
              }
            }
            
            console.log('[TestTakerWrapper] Valid response found from URL:', response);
            setTestResponseId(response.id);
            setIsInitialized(true);
            return;
          } else {
            console.warn('[TestTakerWrapper] URL responseId invalid or unauthorized');
            toast({
              title: "Cannot Resume",
              description: "This test response is not available. Starting fresh.",
              variant: "destructive",
            });
          }
        }

        // PRIORITY 2: First check if user is creator or collaborator, and fetch organization context
        const [testQuery, collaboratorQuery] = await Promise.all([
          supabase
            .from('tests')
            .select('creator_id, organization_id')
            .eq('id', testId)
            .single(),
          supabase
            .from('test_collaborators')
            .select('id')
            .eq('test_id', testId)
            .eq('collaborator_id', user.id)
            .eq('status', 'accepted')
            .maybeSingle()
        ]);

        const isCreator = testQuery.data?.creator_id === user.id;
        const isCollaborator = !!collaboratorQuery.data;
        const organizationId = testQuery.data?.organization_id;
        setIsUserPreview(isCreator || isCollaborator);

        // Check for any previous responses (completed or in-progress) if user is previewing
        if (isCreator || isCollaborator) {
          const { data: allPreviousResponses, error: prevError } = await supabase
            .from('test_responses')
            .select('id, status')
            .eq('test_id', testId)
            .eq('candidate_id', user.id);

          if (prevError) {
            console.error('[TestTakerWrapper] Error checking previous responses:', prevError);
          } else if (allPreviousResponses && allPreviousResponses.length > 0) {
            const completedCount = allPreviousResponses.filter(r => r.status === 'completed').length;
            const inProgressResponse = allPreviousResponses.find(r => r.status === 'in_progress');
            
            // If there's an in-progress response, use it
            if (inProgressResponse) {
              console.log('[TestTakerWrapper] Found existing in-progress response for preview:', inProgressResponse);
              setTestResponseId(inProgressResponse.id);
              setIsInitialized(true);
              return;
            }
            
            // If there are completed responses, show warning before creating new one
            if (completedCount > 0) {
              setPreviousResponseCount(completedCount);
              setShowPreviewWarning(true);
              return;
            }
          }
        } else {
          // For regular candidates, check for existing in-progress response only
          console.log('[TestTakerWrapper] Checking for existing in-progress test response');
          const { data: existingResponses, error: existingError } = await supabase
            .from('test_responses')
            .select()
            .eq('test_id', testId)
            .eq('candidate_id', user.id)
            .eq('status', 'in_progress')
            .maybeSingle();

          if (existingError) {
            console.error('[TestTakerWrapper] Error checking existing responses:', existingError);
            toast({
              title: "Error",
              description: "Failed to check test status. Please try again.",
              variant: "destructive",
            });
            return;
          }

          if (existingResponses) {
            console.log('[TestTakerWrapper] Found existing in-progress response:', existingResponses);
            setTestResponseId(existingResponses.id);
            setIsInitialized(true);
            return;
          }
        }

        // Create new test response
        await createNewTestResponse();
      } catch (error: any) {
        console.error('[TestTakerWrapper] Error in createTestResponse:', error);
        toast({
          title: "Error",
          description: "Failed to start test. Please try again.",
          variant: "destructive",
        });
      }
    };

    createTestResponse();
  }, [user, testId, urlResponseId, toast]);

  if (showPreviewWarning) {
    return (
      <PreviewWarningDialog
        open={showPreviewWarning}
        onConfirm={handlePreviewWarningConfirm}
        onCancel={handlePreviewWarningCancel}
        previousResponseCount={previousResponseCount}
      />
    );
  }

  if (!user) {
    console.log('[TestTakerWrapper] Redirecting to login - no user');
    return <Navigate to={`/login?returnTo=${encodeURIComponent(window.location.pathname)}`} replace />;
  }

  if (!testResponseId) {
    console.log('[TestTakerWrapper] Showing loading state');
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
    </div>;
  }

  console.log('[TestTakerWrapper] Rendering TestTaker component', {
    testId,
    testResponseId
  });

  return testId ? <TestTaker testId={testId} testResponseId={testResponseId} isPreview={isUserPreview} /> : null;
}

function App() {
  // Log auth state on app initialization (debug only)
  useEffect(() => {
    console.log("App initializing, checking auth state...");
    setTimeout(logAuthState, 1000); 
  }, []);

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ProfileProvider>
            <SubscriptionProvider>
              <div className="min-h-screen flex flex-col w-full">
                <Routes>
                  {/* Public routes with PublicLayout */}
                  {/* Auth routes without nav */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/innoventure-login" element={<InnoventureLogin />} />
                  <Route path="/innoventure-app" element={<InnoventureApp />} />
                  <Route path="/innoventure-test/:testId" element={<InnoventureTestDetails />} />
                  <Route path="/innoventure-profile" element={<InnoventureProfile />} />
                  <Route path="/innoventure-test-success" element={<InnoventureTestSuccess />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/password-reset" element={<PasswordReset />} />
                  <Route path="/reset-password" element={<Navigate to="/password-reset" replace />} />
                  <Route path="/verify-otp" element={<VerifyOtp />} />
                  <Route path="/role-selection" element={<RoleSelection />} />
                  <Route path="/auth/calendly/callback" element={<CalendlyCallback />} />

                  {/* Public routes with PublicLayout */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<Index />} />
                    <Route path="/ads" element={<AdsLanding />} />
                    <Route path="/new-landing" element={<NewLanding />} />
                    
                    {/* Add demo success page route */}
                    <Route path="/demo-success" element={<DemoSuccessPage />} />
                    
                    {/* Add blog route */}
                    <Route path="/blog" element={<Blog />} />
                    
                    {/* Policy pages */}
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    
                    {/* Move jobs and job/:jobId routes to public layout */}
                    <Route path="/jobs" element={<JobList />} />
                    <Route path="/job/:jobId" element={<Job />} />
                  </Route>

                  {/* TestCreator routes */}
                  <Route path="/test-creator" element={<TestCreator />} />
                  <Route path="/test-creator/:testId" element={<TestCreator />} />
                  <Route path="/test-creator/success/:successId" element={<TestCreator />} />
                  
                  {/* Auth routes without nav */}
                  <Route path="/email-confirmed" element={<EmailConfirmed />} />
                  <Route path="/test/invite/:token" element={<TestInviteHandler />} />
                  
                  {/* Interview availability route - public access with token */}
                  <Route path="/interview-availability/:responseId" element={<InterviewAvailability />} />
                  
                  {/* Test start handler - converts share token to test ID */}
                  <Route path="/test-start/:token" element={<TestStartHandler />} />
                  
                  {/* Test taking route without navigation */}
                  <Route path="/test/:testId/take" element={<TestTakerWrapper />} />
                  
                  {/* Test submission success page */}
                  <Route path="/test-submission-success" element={<TestSubmissionSuccess />} />
                  
                  {/* Protected routes with MainLayout */}
                  <Route element={<MainLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/test-history" element={<TestHistoryPage />} />
                    <Route path="/job/create" element={<JobCreate />} />
                    <Route path="/test/:testId" element={<TestDetails />} />
                    <Route path="/test-management" element={<TestManagement />} />
                    {/* In-app interview availability route - authenticated access */}
                    <Route path="/availability/:responseId" element={<InterviewAvailability />} />
                    <Route path="/test-response/:responseId" element={<TestResponseView />} />
                    <Route path="/admin/bulk-import" element={<BulkUserImport />} />
                  </Route>
                </Routes>
                <Toaster />
              </div>
            </SubscriptionProvider>
          </ProfileProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
